import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import ReviewForm from '../components/ReviewForm';
import ReviewsList from '../components/ReviewsList';
import { BookOpen, ArrowLeft, Download, ExternalLink, Star, Tag, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { trackEvent } from '../components/GoogleAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StrategyDetail = () => {
  const { strategyId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [strategy, setStrategy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);

  useEffect(() => {
    fetchStrategy();
    fetchReviews();
    if (user) {
      checkLiked();
    }
  }, [strategyId, user]);

  const fetchStrategy = async () => {
    try {
      const response = await axios.get(`${API}/betting-strategies/${strategyId}`);
      setStrategy(response.data);
      
      // Track strategy view
      trackEvent('strategy_viewed', 'Content', response.data.title || strategyId);
    } catch (error) {
      console.error('Error fetching strategy:', error);
      toast.error('Strategy not found');
      navigate('/betting-strategies');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/strategy/${strategyId}`);
      setReviews(response.data.reviews);
      setAverageRating(response.data.average_rating);
      setTotalReviews(response.data.total_reviews);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const checkLiked = async () => {
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.get(`${API}/likes/check?item_id=${strategyId}&item_type=strategy`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLiked(response.data.liked);
    } catch (error) {
      console.error('Error checking like status:', error);
    }
  };

  const handleToggleLike = async () => {
    if (!user) {
      toast.error('Please login to like strategies');
      navigate('/login');
      return;
    }

    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.post(
        `${API}/likes/toggle?item_id=${strategyId}&item_type=strategy`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLiked(response.data.liked);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'System': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'Method': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'Strategy': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'text-green-400';
      case 'Intermediate': return 'text-yellow-400';
      case 'Advanced': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-white text-xl">Loading strategy...</div>
        </div>
      </Layout>
    );
  }

  if (!strategy) return null;

  // Process content to handle tab-separated tables
  const processContent = (content) => {
    if (!content) return '';
    
    // Split content into lines
    const lines = content.split('\n');
    let processedLines = [];
    let inTable = false;
    let tableRows = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const hasTabs = line.includes('\t');
      
      if (hasTabs) {
        // This is a table row
        if (!inTable) {
          inTable = true;
          tableRows = [];
        }
        tableRows.push(line);
      } else {
        // Not a table row
        if (inTable && tableRows.length > 0) {
          // End of table, convert to HTML
          const tableHtml = convertToTable(tableRows);
          processedLines.push(tableHtml);
          tableRows = [];
          inTable = false;
        }
        processedLines.push(line);
      }
    }
    
    // Handle any remaining table
    if (inTable && tableRows.length > 0) {
      const tableHtml = convertToTable(tableRows);
      processedLines.push(tableHtml);
    }
    
    return processedLines.join('\n');
  };
  
  const convertToTable = (rows) => {
    if (rows.length === 0) return '';
    
    let html = '<div class="overflow-x-auto -mx-2 sm:mx-0"><table class="min-w-full border-collapse border border-white/20 my-4 text-sm">';
    
    rows.forEach((row, index) => {
      const cells = row.split('\t').filter(cell => cell.trim());
      const tag = index === 0 ? 'th' : 'td';
      const bgClass = index === 0 ? 'bg-white/10' : 'bg-white/5';
      
      html += '<tr>';
      cells.forEach(cell => {
        html += `<${tag} class="border border-white/20 px-2 py-1 ${bgClass} whitespace-nowrap">${cell}</${tag}>`;
      });
      html += '</tr>';
    });
    
    html += '</table></div>';
    return html;
  };

  const processedContent = processContent(strategy.content);

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          <Button
            onClick={() => navigate('/betting-strategies')}
            variant="ghost"
            className="text-white mb-6 hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Strategies
          </Button>

          <Card className="glass-dark border-white/10 overflow-hidden">
            <CardHeader>
              <div className="flex justify-between items-start mb-4">
                <div className="flex flex-wrap items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getCategoryColor(strategy.category)}`}>
                    {strategy.category}
                  </span>
                  <span className={`text-sm font-medium ${getDifficultyColor(strategy.difficulty)}`}>
                    {strategy.difficulty}
                  </span>
                  {strategy.is_featured && (
                    <div className="flex items-center text-yellow-400">
                      <Star className="w-4 h-4 fill-yellow-400 mr-1" />
                      <span className="text-sm">Featured</span>
                    </div>
                  )}
                </div>
                <Button
                  onClick={handleToggleLike}
                  variant="ghost"
                  className={`${liked ? 'text-red-400' : 'text-gray-400'} hover:text-red-400`}
                >
                  <Heart className={`w-6 h-6 ${liked ? 'fill-red-400' : ''}`} />
                </Button>
              </div>

              <CardTitle className="text-3xl text-white mb-4">{strategy.title}</CardTitle>
              
              {/* Rating Display */}
              {totalReviews > 0 && (
                <div className="flex items-center space-x-2 mb-4">
                  <div className="flex items-center">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-5 h-5 ${
                          star <= Math.round(averageRating)
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-gray-600'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-white font-medium">{averageRating}</span>
                  <span className="text-gray-400">({totalReviews} {totalReviews === 1 ? 'review' : 'reviews'})</span>
                </div>
              )}
              
              <p className="text-gray-300 text-lg">{strategy.description}</p>

              {strategy.tags && strategy.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  <Tag className="w-4 h-4 text-gray-400 mr-1" />
                  {strategy.tags.map((tag, idx) => (
                    <span key={idx} className="text-sm px-3 py-1 rounded-full glass text-gray-300">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Download/External Links */}
              <div className="flex gap-3 mt-6">
                {strategy.pdf_url && (
                  <Button
                    onClick={() => window.open(`${BACKEND_URL}${strategy.pdf_url}`, '_blank')}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                )}
                {strategy.source_url && (
                  <Button
                    onClick={() => window.open(strategy.source_url, '_blank')}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Original Source
                  </Button>
                )}
              </div>
            </CardHeader>

            <CardContent className="overflow-hidden">
              {strategy.image_url && (
                <div className="mb-6 rounded-lg overflow-hidden">
                  <img 
                    src={strategy.image_url} 
                    alt={strategy.title} 
                    className="w-full h-auto max-w-full"
                    loading="lazy"
                    style={{ maxWidth: '100%', height: 'auto' }}
                  />
                </div>
              )}

              {/* Main Content */}
              <div className="prose prose-invert max-w-none overflow-hidden">
                <style>{`
                  .strategy-content table {
                    display: block;
                    width: 100%;
                    overflow-x: auto;
                    -webkit-overflow-scrolling: touch;
                    font-size: 0.875rem;
                  }
                  .strategy-content pre {
                    overflow-x: auto;
                    max-width: 100%;
                  }
                  @media (max-width: 768px) {
                    .strategy-content {
                      font-size: 0.875rem;
                    }
                    .strategy-content table {
                      font-size: 0.75rem;
                    }
                  }
                `}</style>
                <div 
                  className="strategy-content text-gray-300 leading-relaxed whitespace-pre-wrap break-words"
                  style={{ 
                    wordBreak: 'break-word', 
                    overflowWrap: 'break-word',
                    maxWidth: '100%',
                    width: '100%'
                  }}
                  dangerouslySetInnerHTML={{ __html: processedContent }}
                />
              </div>

              {/* Meta Information */}
              <div className="mt-8 pt-6 border-t border-white/10">
                <div className="flex justify-between text-sm text-gray-400">
                  <span>Created by: {strategy.created_by}</span>
                  <span>Updated: {new Date(strategy.updated_at).toLocaleDateString()}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Reviews Section */}
          <Card className="glass-dark border-white/10 mt-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Star className="w-5 h-5 mr-2 text-yellow-400" />
                Reviews ({totalReviews})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {user ? (
                <div>
                  <h3 className="text-white font-medium mb-4">Write a Review</h3>
                  <ReviewForm
                    itemId={strategyId}
                    itemType="strategy"
                    onReviewSubmitted={fetchReviews}
                  />
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-400 mb-3">Please login to write a review</p>
                  <Button
                    onClick={() => navigate('/login')}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Login
                  </Button>
                </div>
              )}

              <div className="border-t border-white/10 pt-6">
                <h3 className="text-white font-medium mb-4">User Reviews</h3>
                <ReviewsList
                  reviews={reviews}
                  currentUserId={user?.id}
                  isAdmin={user?.is_admin}
                  onReviewDeleted={fetchReviews}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default StrategyDetail;
